import React, { useEffect, useMemo, useState } from "react";
import { useParams, useOutletContext } from "react-router-dom";
import { motion, useSpring, useTransform, AnimatePresence } from "framer-motion";
import "./DwellTime.css";
import { useLotAnalytics, useLotHourlyData } from "../../hooks/useLotData";
import { useMultiLot } from "../../contexts/MultiLotContext";
import { useMultiLotAnalytics, useMultiLotHourlyData } from "../../hooks/useMultiLotData";

const BAR_COLORS = ["var(--parallel-blue)", "var(--graph-yellow)", "var(--graph-cyan)", "var(--graph-green)", "var(--graph-red)"];

const METRIC_CONFIG = [
    {
        key: "averageRegisteredVehicleDwellTime",
        label: "Registered Vehicles",
        color: BAR_COLORS[0],
    },
    {
        key: "averageNewVisitorVehicleDwellTime",
        label: "New Visitors",
        color: BAR_COLORS[1],
    },
    {
        key: "averageReturnVehiclesDwellTime",
        label: "Return Visitors",
        color: BAR_COLORS[2],
    },
    {
        key: "averageElectricVehiclesDwellTime",
        label: "Electric Vehicles",
        color: BAR_COLORS[3],
    },
    {
        key: "averageViolatorDwellTime",
        label: "Violators",
        color: BAR_COLORS[4],
    },
];

function formatMinutes(mins: number) {
    if (mins < 60) return `${Math.round(mins)} mins`;
    const h = Math.floor(mins / 60);
    const m = Math.round(mins % 60);
    return `${h}h${m > 0 ? ` ${m}m` : ""}`;
}

function percentChange(current: number, prev: number) {
    if (prev === 0) return current === 0 ? 0 : 100;
    return ((current - prev) / Math.abs(prev)) * 100;
}

function getTimeRangeText(timeframe: string, startDate: string, endDate: string) {
    // Parse the input dates
    const [startYear, startMonth, startDay] = startDate.split('-').map(Number);
    const [endYear, endMonth, endDay] = endDate.split('-').map(Number);

    // Create dates in local timezone
    const start = new Date(startYear, startMonth - 1, startDay);
    const end = new Date(endYear, endMonth - 1, endDay);

    // Get current date in local timezone
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    if (timeframe === "day") {
        if (start.getTime() === today.getTime()) {
            return "Today";
        }
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        if (start.getTime() === yesterday.getTime()) {
            return "Yesterday";
        }
        return `on ${start.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}`;
    }

    if (timeframe === "week") {
        // Calculate current week boundaries
        const currentWeekStart = new Date(today);
        currentWeekStart.setDate(today.getDate() - today.getDay());
        const currentWeekEnd = new Date(currentWeekStart);
        currentWeekEnd.setDate(currentWeekStart.getDate() + 6);

        // Calculate last week boundaries
        const lastWeekStart = new Date(currentWeekStart);
        lastWeekStart.setDate(currentWeekStart.getDate() - 7);
        const lastWeekEnd = new Date(lastWeekStart);
        lastWeekEnd.setDate(lastWeekStart.getDate() + 6);

        if (start.getTime() === currentWeekStart.getTime() && end.getTime() === currentWeekEnd.getTime()) {
            return "This Week";
        }
        if (start.getTime() === lastWeekStart.getTime() && end.getTime() === lastWeekEnd.getTime()) {
            return "Last Week";
        }
        return `${start.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`;
    }

    if (timeframe === "month") {
        // Calculate current month boundaries
        const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        const currentMonthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);

        // Calculate last month boundaries
        const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);

        if (start.getTime() === currentMonthStart.getTime() && end.getTime() === currentMonthEnd.getTime()) {
            return "This Month";
        }
        if (start.getTime() === lastMonthStart.getTime() && end.getTime() === lastMonthEnd.getTime()) {
            return "Last Month";
        }
        return start.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }

    if (timeframe === "year") {
        // Calculate current year boundaries
        const currentYearStart = new Date(today.getFullYear(), 0, 1);
        const currentYearEnd = new Date(today.getFullYear(), 11, 31);

        // Calculate last year boundaries
        const lastYearStart = new Date(today.getFullYear() - 1, 0, 1);
        const lastYearEnd = new Date(today.getFullYear() - 1, 11, 31);

        if (start.getTime() === currentYearStart.getTime() && end.getTime() === currentYearEnd.getTime()) {
            return "This Year";
        }
        if (start.getTime() === lastYearStart.getTime() && end.getTime() === lastYearEnd.getTime()) {
            return "Last Year";
        }
        return start.getFullYear().toString();
    }

    return "";
}

function getChangeText(timeframe: string, startDate: string) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);

    if (timeframe === "day") {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        if (start.getTime() === yesterday.getTime()) return "Yesterday";
        return "Previous Day";
    }

    if (timeframe === "week") {
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        const lastWeek = new Date(weekStart);
        lastWeek.setDate(lastWeek.getDate() - 7);
        if (start.getTime() === lastWeek.getTime()) return "Last Week";
        return "Previous Week";
    }

    if (timeframe === "month") {
        const currentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        if (start.getTime() === lastMonth.getTime()) return "Last Month";
        return "Previous Month";
    }

    if (timeframe === "year") {
        const currentYear = new Date(today.getFullYear(), 0, 1);
        const lastYear = new Date(today.getFullYear() - 1, 0, 1);
        if (start.getTime() === lastYear.getTime()) return "Last Year";
        return "Previous Year";
    }

    return "Previous Period";
}

const DwellTime = () => {
    const { lotId } = useParams<{ lotId: string }>();
    const { timeframe, currentRange, previousRange } = useOutletContext<any>();
    const [activeIndex, setActiveIndex] = useState<number>(-1);

    // Multi-lot context
    const { isMultiLotMode, getSelectedLotIds } = useMultiLot();
    const selectedLotIds = isMultiLotMode ? getSelectedLotIds() : [lotId || ""];

    // Single-lot hooks (always called to maintain hook order)
    const singleLotCurrentAnalytics = useLotAnalytics(
        lotId || "",
        currentRange.startDate,
        currentRange.endDate,
        timeframe
    );

    const singleLotPreviousAnalytics = useLotAnalytics(
        lotId || "",
        previousRange.startDate,
        previousRange.endDate,
        timeframe
    );

    const singleLotHourlyData = useLotHourlyData(
        lotId || "",
        currentRange.startDate,
        timeframe
    );

    // Multi-lot hooks (always called to maintain hook order)
    const multiLotCurrentAnalytics = useMultiLotAnalytics(
        selectedLotIds,
        currentRange.startDate,
        currentRange.endDate,
        timeframe
    );

    const multiLotPreviousAnalytics = useMultiLotAnalytics(
        selectedLotIds,
        previousRange.startDate,
        previousRange.endDate,
        timeframe
    );

    const multiLotHourlyData = useMultiLotHourlyData(
        selectedLotIds,
        currentRange.startDate,
        timeframe
    );

    // Select appropriate data based on mode
    const currentMetricsData = isMultiLotMode ? multiLotCurrentAnalytics.data : singleLotCurrentAnalytics.data;
    const previousMetricsData = isMultiLotMode ? multiLotPreviousAnalytics.data : singleLotPreviousAnalytics.data;
    const hourlyDataData = isMultiLotMode ? multiLotHourlyData.data : singleLotHourlyData.data;

    const isCurrentLoading = isMultiLotMode ? multiLotCurrentAnalytics.isLoading : singleLotCurrentAnalytics.isLoading;
    const isCurrentFetching = isMultiLotMode ? multiLotCurrentAnalytics.isFetching : singleLotCurrentAnalytics.isFetching;
    const isPreviousLoading = isMultiLotMode ? multiLotPreviousAnalytics.isLoading : singleLotPreviousAnalytics.isLoading;
    const isPreviousFetching = isMultiLotMode ? multiLotPreviousAnalytics.isFetching : singleLotPreviousAnalytics.isFetching;
    const isHourlyLoading = isMultiLotMode ? multiLotHourlyData.isLoading : singleLotHourlyData.isLoading;
    const isHourlyFetching = isMultiLotMode ? multiLotHourlyData.isFetching : singleLotHourlyData.isFetching;

    // Use hourly data if available and it's today
    const effectiveCurrentData = useMemo(() => {
        if (timeframe === 'day' && hourlyDataData?.data && hourlyDataData.data.length > 0) {
            return hourlyDataData.data;
        }
        return currentMetricsData;
    }, [timeframe, hourlyDataData, currentMetricsData]);

    // Aggregate metrics
    const metrics = useMemo(() => {
        if (!effectiveCurrentData || effectiveCurrentData.length === 0) return {};

        const agg: Record<string, number> = {};

        if (timeframe === 'day' && hourlyDataData?.data && hourlyDataData.data.length > 0) {
            // For hourly data, calculate means excluding zeros
            METRIC_CONFIG.forEach(({ key }) => {
                const validEntries = hourlyDataData.data.filter((entry: Record<string, any>) => Number(entry[key]) > 0);
                const sum = validEntries.reduce((sum: number, entry: Record<string, any>) => sum + (Number(entry[key]) || 0), 0);
                agg[key] = validEntries.length > 0 ? sum / validEntries.length : 0;
            });

            // Average vehicle dwell time is also a mean, excluding zeros
            const validDwellEntries = hourlyDataData.data.filter((entry: Record<string, any>) => Number(entry["averageVehicleDwellTime"]) > 0);
            const totalDwellTime = validDwellEntries.reduce((sum: number, entry: Record<string, any>) => sum + (Number(entry["averageVehicleDwellTime"]) || 0), 0);
            agg["averageVehicleDwellTime"] = validDwellEntries.length > 0 ? totalDwellTime / validDwellEntries.length : 0;
        } else {
            // For other timeframes, use daily data excluding zeros
            METRIC_CONFIG.forEach(({ key }) => {
                const validEntries = effectiveCurrentData.filter((entry: Record<string, any>) => Number(entry[key]) > 0);
                const sum = validEntries.reduce((sum: number, entry: Record<string, any>) => sum + (Number(entry[key]) || 0), 0);
                agg[key] = validEntries.length > 0 ? sum / validEntries.length : 0;
            });

            // Average vehicle dwell time is also a mean, excluding zeros
            const validDwellEntries = effectiveCurrentData.filter((entry: Record<string, any>) => Number(entry["averageVehicleDwellTime"]) > 0);
            const totalDwellTime = validDwellEntries.reduce((sum: number, entry: Record<string, any>) => sum + (Number(entry["averageVehicleDwellTime"]) || 0), 0);
            agg["averageVehicleDwellTime"] = validDwellEntries.length > 0 ? totalDwellTime / validDwellEntries.length : 0;
        }

        return agg;
    }, [effectiveCurrentData, timeframe, hourlyDataData]);

    const prevMetrics = useMemo(() => {
        if (!previousMetricsData || previousMetricsData.length === 0) return {};

        const agg: Record<string, number> = {};
        METRIC_CONFIG.forEach(({ key }) => {
            const sum = previousMetricsData.reduce((sum: number, entry: Record<string, any>) => sum + (Number(entry[key]) || 0), 0);
            agg[key] = previousMetricsData.length > 0 ? sum / previousMetricsData.length : 0;
        });

        // Average vehicle dwell time is also a mean
        const totalDwellTime = previousMetricsData.reduce((sum: number, entry: Record<string, any>) => sum + (Number(entry["averageVehicleDwellTime"]) || 0), 0);
        agg["averageVehicleDwellTime"] = previousMetricsData.length > 0 ? totalDwellTime / previousMetricsData.length : 0;

        return agg;
    }, [previousMetricsData]);

    // Animated value for main metric
    const animatedMain = useSpring(metrics["averageVehicleDwellTime"] || 0, { stiffness: 10000, damping: 600 });
    useEffect(() => {
        if (timeframe === timeframe) {
            animatedMain.set(metrics["averageVehicleDwellTime"] || 0);
        }
    }, [metrics["averageVehicleDwellTime"], timeframe]);
    const displayMain = useTransform(animatedMain, v => formatMinutes(v));

    // Bar data: only the 5 metrics (not averageVehicleDwellTime)
    const barData = useMemo(() => {
        if (timeframe !== timeframe) return [];

        return METRIC_CONFIG.map((cfg, i) => ({
            ...cfg,
            value: metrics[cfg.key] || 0,
            prev: prevMetrics[cfg.key] || 0,
            change: percentChange(metrics[cfg.key] || 0, prevMetrics[cfg.key] || 0),
            idx: i,
        }));
    }, [metrics, prevMetrics, timeframe]);

    // Find the max value for proportional bar widths
    const maxBarValue = Math.max(...barData.map(b => b.value), 1);

    const prevLabel = getChangeText(timeframe, previousRange?.startDate);

    const hasDwellData =
        (timeframe === "day" && hourlyDataData?.data && hourlyDataData.data.length > 0) ||
        (currentMetricsData && currentMetricsData.length > 0);

    const isLoading =
        isCurrentLoading ||
        isPreviousLoading ||
        isHourlyLoading ||
        isCurrentFetching ||
        isPreviousFetching ||
        isHourlyFetching ||
        !metrics ||
        !hasDwellData;

    if (isLoading) {
        return (
            <div className="dwell-time-content dwell-skeleton">
                <div className="skeleton-block dwell-skeleton-block" aria-hidden="true" />
            </div>
        );
    }

    return (
        <div className="dwell-time-content">
            <div className="dwell-time-metric-box">
                <div className="dwell-time-metric-value">
                    <motion.span>{displayMain}</motion.span>
                </div>
                <div className="dwell-time-metric-title">
                    Average Dwell Time {getTimeRangeText(timeframe, currentRange.startDate, currentRange.endDate)}
                </div>
                <div className="dwell-time-metric-desc">Last week, on average vehicles parked for {formatMinutes(metrics["averageVehicleDwellTime"] || 0)}.</div>
            </div>
            <div className="dwell-time-bar-graph-box">
                {barData.map((bar, idx) => (
                    <div key={bar.key} className="dwell-time-bar-row">
                        <div className="dwell-time-bar-outer">
                            <motion.div
                                className="dwell-time-bar-inner"
                                initial={{ width: 0 }}
                                animate={{ width: `${(bar.value / maxBarValue) * 100}%` }}
                                transition={{ duration: 0.5, delay: idx * 0.1 }}
                                style={{
                                    background: bar.color,
                                }}
                            />
                        </div>
                        <div className="dwell-time-bar-title">
                            <span className="dwell-time-bar-label" style={{ color: "var(--text-dark)" }}>{bar.label}:</span>
                            <span className="dwell-time-bar-value">{formatMinutes(bar.value)}</span>
                        </div>
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={bar.change}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                transition={{ duration: 0.2 }}
                                className={`dwell-time-bar-change ${bar.change > 0 ? "change-up" : bar.change < 0 ? "change-down" : "change-same"}`}
                            >
                                {bar.change > 0 ? "+" : bar.change < 0 ? "-" : ""}{Math.abs(bar.change).toFixed(1)}% {getChangeText(timeframe, previousRange.startDate)}
                            </motion.div>
                        </AnimatePresence>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default DwellTime;
